import { useQueryClient } from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import {
  fetchInvoiceParties,
  type InvoiceDirection,
  type InvoiceParty,
  type InvoicePartyRole,
  queryKeysInvoice,
} from '~/data/invoice';

import { SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ name }: InvoiceParty) => name;

type P = {
  readonly direction: InvoiceDirection;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly onChange: (ids: UUID[], parties: InvoiceParty[]) => void;
  readonly partyRole: InvoicePartyRole;
  readonly placeholder?: string;
  readonly value: UUID[];
} & ComponentStyling;

/**
 * MultiSelect for the companies that appear as parties (buyer or seller) on
 * the invoices accessible to the user. Backed by
 * GET /invoice/filter_options/parties, which permission-scopes its results by
 * direction — unlike the company directory behind MultiSelectCompanies.
 */
export const MultiSelectInvoiceParties = ({
  className,
  direction,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  onChange,
  partyRole,
  placeholder,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  const loadOptions = createLoadOptions<InvoiceParty>({
    createQueryKey: ({ offset, searchString }) =>
      queryKeysInvoice.getFilterParties({
        direction,
        offset,
        partyRole,
        searchString: searchString || undefined,
      }),
    async fetchItems({ offset, searchString }) {
      const { data, hasNextPage } = await fetchInvoiceParties({
        direction,
        offset,
        partyRole,
        searchString: searchString || undefined,
      });

      return {
        data,
        hasNextPage,
      };
    },
    queryClient,
    // The global staleTime is 0, which would refetch identical option pages on
    // every dropdown open; match getInvoicePartiesQueryOptions instead.
    queryOptions: { staleTime: ms('60s') },
  });

  /**
   * Resolves a selected company id to its display data, e.g. when restoring a
   * saved filter. Uses the endpoint's company_ids hydration mode; ids that are
   * not (or no longer) accessible reject and the select drops the chip.
   */
  const getItemData = createGetItemData<InvoiceParty>({
    createQueryKey: (companyId) =>
      queryKeysInvoice.getFilterParty({ companyId, direction, partyRole }),
    async fetchItem(companyId) {
      const { data } = await fetchInvoiceParties({
        companyIds: [companyId],
        direction,
        partyRole,
      });
      const party = data.find(({ id }) => id === companyId);

      if (!party) {
        throw new Error(`Invoice party ${companyId} is not accessible`);
      }

      return party;
    },
    queryClient,
    queryKeyBase: [queryKeysInvoice.base(), 'filter-parties'],
  });

  // Normalizes SelectServerDriven's single/multi overload to plain arrays.
  const handleChange = (
    newValues: string[] | string,
    data: InvoiceParty[] | InvoiceParty,
  ) => {
    onChange([newValues].flat(), [data].flat());
  };

  return (
    <SelectServerDriven<InvoiceParty>
      className={className}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={() => true}
      isMultiSelect={isMultiSelect}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
